import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  memo,
  type ReactNode,
  type PropsWithChildren,
  type ComponentType,
  type ElementType,
} from 'react';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import {
  Heading,
  NakedButton,
  NotificationBadge,
  PopoverMenu,
  PopoverMenuItem,
  styles,
  usePopoverMenu,
  type HeadingProps,
} from '@frontend/design-system';

type SubNavItem = {
  id: string;
  label: string;
  to?: string;
  count?: number;
  isPassiveCount?: boolean;
};

type LinkComponentProps = {
  to: string;
  children: ReactNode;
  preload?: 'intent' | 'render' | 'none';
  preloadIntentProximity?: number;
};

type HeaderContextValue = {
  activeId?: string;
  onSelect?: (item: SubNavItem) => void;
  linkComponent?: ComponentType<LinkComponentProps>;
};

const HeaderContext = createContext<HeaderContextValue | null>(null);

const useHeaderContext = () => {
  const context = useContext(HeaderContext);
  if (!context) {
    throw new Error('Header compound components must be used within Header.Root');
  }
  return context;
};

type RootProps = PropsWithChildren<
  Omit<HeadingProps, 'children'> & {
    activeId?: string;
    onSelect?: (item: SubNavItem) => void;
    linkComponent?: ComponentType<any>;
  }
>;

const Root = memo(({ children, activeId, onSelect, linkComponent, ...headingProps }: RootProps) => {
  const contextValue = useMemo(() => ({ activeId, onSelect, linkComponent }), [activeId, onSelect, linkComponent]);

  return (
    <HeaderContext.Provider value={contextValue}>
      <Heading
        level={1}
        {...headingProps}
        css={[
          css`
            @container (max-width: ${breakpoints.small.max}px) {
              font-size: ${theme.fontSize(24)};
            }

            @container (min-width: ${breakpoints.small.max}px) {
              font-size: ${theme.fontSize(32)};
            }
          `,
          styles.truncate,
        ]}
      >
        {children}
      </Heading>
    </HeaderContext.Provider>
  );
});

Root.displayName = 'Header.Root';

type TitleProps = {
  children: ReactNode;
  textAlign?: 'left' | 'center' | 'right' | 'inherit';
};

const Title = memo(({ children, textAlign = 'left' }: TitleProps) => (
  <span
    css={[
      css`
        text-align: ${textAlign};
        @container (max-width: ${breakpoints.small.max}px) {
          font-size: ${theme.fontSize(24)};
        }

        @container (min-width: ${breakpoints.small.max}px) {
          font-size: ${theme.fontSize(32)};
        }
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        min-width: 0;
      `,
    ]}
  >
    {children}
  </span>
));

Title.displayName = 'Header.Title';

type BadgeProps = {
  count?: number;
  isPassive?: boolean;
};

const Badge = memo(({ count, isPassive }: BadgeProps) => {
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });

  if (!count || !isMobile) return null;

  return (
    <NotificationBadge
      css={[
        css`
          flex: unset !important;
          color: ${theme.colors.white} !important;
          margin-left: ${theme.spacing(1)};
        `,
        isPassive &&
          css`
            background: ${theme.colors.primary10};
            border-radius: ${theme.borderRadius.small};
            color: ${theme.colors.primary70} !important;
          `,
      ]}
      truncateCount
    >
      {count}
    </NotificationBadge>
  );
});

Badge.displayName = 'Header.Badge';

type SubNavProps = {
  children: ReactNode;
  items: SubNavItem[];
};

const SubNav = memo(({ children, items }: SubNavProps) => {
  const { activeId, onSelect, linkComponent } = useHeaderContext();
  const { getMenuProps, getTriggerProps, getItemProps, isOpen, close } = usePopoverMenu({
    placement: 'bottom-start',
    middlewareOptions: { offset: 4 },
  });

  const handleSelect = useCallback(
    (item: SubNavItem) => {
      onSelect?.(item);
      close();
    },
    [onSelect, close]
  );

  return (
    <>
      <NakedButton
        css={[
          css`
            width: 100%;
            display: flex;
            align-items: center;
            :hover .caret-icon,
            :focus .caret-icon {
              background: ${theme.colors.neutral20};
            }
          `,
        ]}
        {...getTriggerProps()}
      >
        {children}
        <span
          css={css`
            display: flex;
            align-items: center;
            margin-left: ${theme.spacing(2)};
            border-radius: ${theme.borderRadius.medium};
            justify-content: center;
            height: 24px;
            width: 24px;
            background: ${isOpen ? theme.colors.primary20 : 'white'};
            transition: background 150ms ease-out;
            color: ${theme.colors.neutral70};
            :hover,
            :focus {
              background: ${theme.colors.neutral20};
            }
          `}
        >
          <Icon
            name='caret-down-small'
            css={css`
              transition: transform 150ms ease-out;
              transform: rotate(${isOpen ? '180deg' : '0deg'});
            `}
          />
        </span>
      </NakedButton>
      <PopoverMenu
        {...getMenuProps()}
        css={css`
          min-width: 220px;
          max-width: 300px;
        `}
      >
        {items.map((item, index) => (
          <SubNavItemComponent
            key={item.id}
            item={item}
            index={index}
            isActive={activeId === item.id}
            getItemProps={getItemProps}
            onSelect={handleSelect}
            linkComponent={linkComponent}
          />
        ))}
      </PopoverMenu>
    </>
  );
});

SubNav.displayName = 'Header.SubNav';

type SubNavItemComponentProps = {
  item: SubNavItem;
  index: number;
  isActive: boolean;
  getItemProps: ReturnType<typeof usePopoverMenu>['getItemProps'];
  onSelect: (item: SubNavItem) => void;
  linkComponent?: ComponentType<LinkComponentProps>;
};

const SubNavItemComponent = memo(
  ({ item, index, isActive, getItemProps, onSelect, linkComponent }: SubNavItemComponentProps) => {
    const handleClick = useCallback(() => onSelect(item), [onSelect, item]);

    const itemProps = {
      css: css`
        color: ${theme.font.colors.default};
        justify-content: space-between;
        width: 100%;
      `,
      ...getItemProps({ index }),
      active: isActive,
      onClick: handleClick,
    };

    const content = (
      <>
        <span css={styles.truncate}>{item.label}</span>
        <Badge count={item.count} isPassive={item.isPassiveCount} />
      </>
    );

    if (linkComponent && item.to) {
      return (
        <PopoverMenuItem
          {...itemProps}
          //@ts-ignore
          //PopoverMenuItem isn't fylly polymorphic
          as={linkComponent as ElementType}
          to={item.to}
          preload='intent'
          preloadIntentProximity={100}
        >
          {content}
        </PopoverMenuItem>
      );
    }

    return <PopoverMenuItem {...itemProps}>{content}</PopoverMenuItem>;
  }
);

SubNavItemComponent.displayName = 'Header.SubNavItemComponent';

type MobileSubNavProps = SubNavProps;

const MobileSubNav = memo((props: MobileSubNavProps) => {
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  if (!isMobile) return <>{props.children}</>;
  return <SubNav {...props} />;
});

MobileSubNav.displayName = 'Header.MobileSubNav';

const headerStyles = css`
  display: flex;
  align-items: center;
  padding: ${theme.spacing(2, 2, 1, 2)};
  justify-content: space-between;
  @container (max-width: ${breakpoints.small.max}px) {
    font-size: ${theme.fontSize(24)};
  }
`;

type PanelProps = PropsWithChildren<RootProps>;

const Panel = memo(({ children, ...rootProps }: PanelProps) => (
  <header css={headerStyles}>
    <Root {...rootProps} />
    {children}
  </header>
));

Panel.displayName = 'Header.Panel';

export const Header = {
  Root,
  Title,
  Badge,
  SubNav,
  MobileSubNav,
  Panel,
};

export type { SubNavItem, LinkComponentProps };
